import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { COAST } from "./coastline";
import { useInView } from "./primitives";

// A black bar where a word or a number would be.
const Bar = ({ ch = 4 }) => <span className="sj-bar" style={{ width: `${ch}ch` }} aria-hidden="true" />;

// Redacted nautical chart. Coastline draws itself in; the name and position stay under black until a code opens them.
export const Chart = ({ reduce, unlocked }) => {
  const [ref, inView] = useInView("0px 0px -20% 0px");
  const pad = 60;
  const W = COAST.w + pad * 2;
  const H = COAST.h + pad * 2;
  const grid = [];
  for (let x = pad; x <= W - pad; x += 130) grid.push(<line key={`v${x}`} x1={x} y1={0} x2={x} y2={H} />);
  for (let y = pad; y <= H - pad; y += 130) grid.push(<line key={`h${y}`} x1={0} y1={y} x2={W} y2={y} />);

  return (
    <figure ref={ref} className={`sj-chart ${inView ? "in" : ""} ${unlocked ? "open" : ""}`} data-testid="sojourns-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Chart of the destination. The name and position are withheld until you are accepted." focusable="false">
        <g className="sj-chart-grid" stroke="#3B352F" strokeWidth="1">{grid}</g>
        <g className="sj-chart-ticks" fill="#000">
          <rect x="12" y={pad + 100} width="34" height="12" /><rect x="12" y={pad + 360} width="34" height="12" /><rect x="12" y={pad + 620} width="34" height="12" />
          <rect x={pad + 120} y={H - 26} width="46" height="12" /><rect x={pad + 510} y={H - 26} width="46" height="12" /><rect x={pad + 900} y={H - 26} width="46" height="12" />
        </g>
        <g transform={`translate(${pad}, ${pad})`}>
          <path className="sj-coast-fill" d={COAST.d} fill="#EDE6DB" fillOpacity="0.035" stroke="none" />
          <path className="sj-coast" d={COAST.d} fill="none" stroke="#EDE6DB" strokeWidth="1.4" strokeLinejoin="round" pathLength="1" />
        </g>
        {/* the land itself is under black until the code opens it */}
        <rect className="sj-land-bar" x={pad + 40} y={pad + 250} width={COAST.w - 80} height="300" fill="#000" data-testid="sojourns-chart-land-bar" />
        <g className="sj-chart-compass" transform={`translate(${W - pad - 40}, ${pad + 70})`} stroke="#8E857B" strokeWidth="1" fill="none">
          <line x1="0" y1="-40" x2="0" y2="40" /><line x1="-40" y1="0" x2="40" y2="0" /><circle r="6" />
          <text x="0" y="-50" textAnchor="middle" fill="#8E857B" stroke="none" fontFamily="Space Mono, monospace" fontSize="14" letterSpacing="2">N</text>
        </g>
        <g className="sj-chart-scale" transform={`translate(${pad}, ${H - pad + 10})`} stroke="#8E857B" strokeWidth="1">
          <line x1="0" y1="0" x2="200" y2="0" /><line x1="0" y1="-6" x2="0" y2="6" /><line x1="100" y1="-6" x2="100" y2="6" /><line x1="200" y1="-6" x2="200" y2="6" />
        </g>
      </svg>
      <figcaption className="sj-chart-legend">
        <div>
          <p className="sj-label">Destination</p>
          <p className="sj-chart-name" data-testid="sojourns-chart-name">
            {unlocked?.destination ? <span className="sj-unredacted">{unlocked.destination}</span> : <Bar ch={9} />}
          </p>
          <p className="sj-chart-coords" data-testid="sojourns-chart-coords">
            {unlocked?.coords ? unlocked.coords : <><Bar ch={2} />°<Bar ch={2} />′N<span className="gap" /><Bar ch={2} />°<Bar ch={2} />′E</>}
          </p>
        </div>
        <p className="sj-chart-note" data-testid="sojourns-chart-note">
          {unlocked ? (unlocked.note || "") : "Revealed to accepted applicants."}
        </p>
      </figcaption>
    </figure>
  );
};

// Full-bleed still: one long table at dusk.
export const Still = () => {
  const [ref, inView] = useInView("200px 0px 200px 0px");
  return (
    <figure ref={ref} className="sj-still" data-testid="sojourns-still">
      <div className="sj-still-frame">
        {inView && (
          <img
            src="/sojourn-media/table-1600.webp"
            srcSet="/sojourn-media/table-800.webp 800w, /sojourn-media/table-1600.webp 1264w"
            sizes="100vw"
            alt="A long table laid for twelve on a stone terrace at dusk, lit by candles."
            loading="lazy"
            decoding="async"
          />
        )}
      </div>
      <figcaption>Dinner is at one long table, cooked by a chef.</figcaption>
    </figure>
  );
};

// "Have an invitation code?" A valid code lifts the redactions on the page.
export const Invite = ({ onUnlock }) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  useEffect(() => { if (open) input.current?.focus(); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/sojourns/unlock`, { code: code.trim() });
      sessionStorage.setItem("sj_unlock", JSON.stringify(r.data));
      onUnlock(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "That code doesn't open anything.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="sj-invite-link" onClick={() => setOpen(true)} data-testid="sojourns-invite-toggle">
        Have an invitation code?
      </button>
    );
  }
  return (
    <form className="sj-invite" onSubmit={submit} noValidate data-testid="sojourns-invite-form">
      <div className="sj-field">
        <label htmlFor="sj-code">Invitation code</label>
        <input ref={input} id="sj-code" type="text" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" spellCheck={false} data-testid="sojourns-invite-code" />
      </div>
      <button type="submit" className="sj-btn ghost" disabled={busy} data-testid="sojourns-invite-submit">{busy ? "Opening" : "Open"}</button>
      {error && <p className="sj-error" role="alert" data-testid="sojourns-invite-error">{error}</p>}
    </form>
  );
};
